import { ButtonCusWithIcon, ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, REGISTER_STATUS_LIST } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { FORM_TYPE, FormType, LANGUAGES_SUPPORTED, REGISTER_STATUS } from '@/types/enums';
import { Location } from '@/types/location';
import { cn } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export default observer(function Locations({ type }: { type: FormType }) {
    return (
        <div className={classNames('grid grid-cols-1 gap-y-4')}>
            <LocationItem type={type} />
        </div>
    );
});

const LocationItem = observer(function LocationItem({ type }: { isRegis?: boolean, type: FormType }) {
    // const
    const isCreate = type === FORM_TYPE.Create;
    const isView = type === FORM_TYPE.View;

    // hook
    const { t, i18n: { language } } = useTranslation();
    const { id } = useParams();

    // store
    const {
        locationStore: { locationBikes, pagingRegister, totalRecordRegister, getLocationsByBike,
            clean, keepSearchParams, setObservable, defaultPaging, getLocations, locations, paging },
        regionProvinceStore: {
            regions, provinces,
            getRegions, getProvinces, getProvincesByRegion
        },
        modalStore: { showAlertModal },
        bikeStore: { bikeDetail, registerLocation, unregisterLocation } } = useStore();

    // get id
    const newId = isCreate ? bikeDetail?.id : id;

    // state
    const [rowSelected, setRowSelected] = useState({});
    const [enableStatus, setEnableStatus] = useState<boolean>(false);
    // validate schema
    const validateSchema = yup.object().shape({
        regionId: yup.string().optional(),
        provinceId: yup.string().optional(),
        isRegis: yup.string().optional().nullable(),
        locationId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            regionId: keepSearchParams?.regionId ?? '',
            provinceId: keepSearchParams?.provinceId ?? '',
            isRegis: keepSearchParams?.isRegis ?? Boolean(REGISTER_STATUS.UNREGISTER).valueOf().toString(),
            locationId: keepSearchParams?.locationId ?? ''
        }
    });

    // columns
    const columns: ColumnDef<Location>[] = [
        {
            accessorKey: 'region',
            header: t('field.region'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='min-w-[100px]'>{language === LANGUAGES_SUPPORTED.Japanese ? (original.region?.nameJP || '') : (original.region?.name || '')}</p>
            )
        },
        {
            accessorKey: 'province',
            header: t('field.prefectures'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='min-w-[100px]'>{language === LANGUAGES_SUPPORTED.Japanese ? (original.province?.nameJP || '') : (original.province?.name || '')}</p>
            )
        },
        {
            accessorKey: 'name',
            header: t('field.location_name'),
            size: 200,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[150px] line-clamp-3'>{original?.name}</div>
            ),
            enableSorting: false
        },
        {
            id: 'actions',
            accessorKey: 'action',
            header: '',
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                original.isRegis ?
                    (
                        <ButtonCusWithIcon
                            disabled={isView}
                            onClick={() => {
                                handleClickUnregister([Number(original.id)], false);
                            }}
                            variant='outline'
                            size='xsm'
                            iconId='#icon-unregister'
                            widthIcon={20}
                            heightIcon={20}
                        >
                            {t('button.unregister')}
                        </ButtonCusWithIcon>
                    ) :
                    (
                        <ButtonCusWithIcon
                            disabled={isView}
                            onClick={() => {
                                handleClickRegister([Number(original.id)], false);
                            }}
                            variant='outlineOrange3'
                            size='xsm'
                            iconId='#icon-register'
                            widthIcon={24}
                            heightIcon={16}
                        >
                            {t('button.register')}
                        </ButtonCusWithIcon>
                    )
            )
        }

    ];

    // life cycle
    useEffect(() => {
        getLocations({}, { ...paging, pagination: { pageIndex: 0, pageSize: -2 } });
        getRegions();
        getProvinces();

        onSearch(keepSearchParams ?? { isRegis: Boolean(REGISTER_STATUS.UNREGISTER).valueOf().toString() });

        return () => {
            clean();
        };
    }, []);

    // function
    const onSearch = (searchParams?: FormData) => {
        if (searchParams?.isRegis && searchParams?.isRegis !== '') {
            setEnableStatus(true);
            setRowSelected({});
        } else {
            setEnableStatus(false);
            setRowSelected({});
        }
        const params: any = { ...searchParams };
        onFetchLocations(params, defaultPaging);
        searchParams && setObservable('keepSearchParams', toJS(searchParams));
    };
    const onFetchLocations = (searchParams?: any, paging?: TableState) => {
        if (!newId) return;
        getLocationsByBike({ bikeId: +newId, ...searchParams }, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        if (!newId) return;
        const newPaging = stateUpdater instanceof Function ?
            stateUpdater(pagingRegister) :
            stateUpdater;
        onFetchLocations(keepSearchParams, newPaging);
    };

    const handleClickRegister = async (locationIds: number[], forRowSelected: boolean = true) => {
        if (!newId) return;
        const res = await flowResult(registerLocation({ locationIds, bikeId: Number(newId) }));
        if (res) {
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.register_success')
            });

            onFetchLocations(keepSearchParams, pagingRegister);

            if (!forRowSelected) {
                let locationId = locationIds[0];
                let listRowSelected = Object.keys(rowSelected).map(key => Number(key));
                if (listRowSelected.includes(locationId)) {
                    setRowSelected(listRowSelected.filter(key => key !== locationId).map(row => ({ row: row })));
                }
            }
            forRowSelected && setRowSelected({});
        }
    };

    const handleClickUnregister = async (locationIds: number[], forRowSelected: boolean = true) => {
        if (!newId) return;
        const res = await flowResult(unregisterLocation({ locationIds, bikeId: Number(newId) }));
        if (res) {
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.unregister_success')
            });

            onFetchLocations(keepSearchParams, pagingRegister);

            if (!forRowSelected) {
                let locationId = locationIds[0];
                let listRowSelected = Object.keys(rowSelected).map(key => Number(key));
                if (listRowSelected.includes(locationId)) {
                    setRowSelected(listRowSelected.filter(key => key !== locationId).map(row => ({ row: row })));
                }
            }
            forRowSelected && setRowSelected({});
        }
    };

    // filter province when change region
    const handleFilterProvince = (regionId: number | undefined) => {
        if (regionId) {
            getProvincesByRegion(regionId);
        } else {
            getProvinces();
        }
    };

    const renderButtonTable = () => {
        return (
            <>
                <ButtonCusWithIcon
                    type='button'
                    disabled={!(enableStatus && (keepSearchParams?.isRegis === Boolean(REGISTER_STATUS.REGISTERED).toString())) && (Object.keys(rowSelected).length === 0 || (keepSearchParams?.isRegis === Boolean(REGISTER_STATUS.UNREGISTER).toString() && Object.keys(rowSelected).length > 0))}
                    onClick={() => {
                        let listRowSelected = Object.keys(rowSelected).map(key => Number(key));
                        handleClickUnregister(listRowSelected, true);
                    }}
                    className='w-full md:h-12 md:w-52'
                    variant='outline'
                    size='md'
                    iconId='#icon-unregister'
                    widthIcon={20}
                    heightIcon={20}
                >
                    {t('button.unregister')}
                </ButtonCusWithIcon>
                <ButtonCusWithIcon
                    disabled={!(enableStatus && (keepSearchParams?.isRegis === Boolean(REGISTER_STATUS.UNREGISTER).toString())) && (Object.keys(rowSelected).length === 0 || (keepSearchParams?.isRegis === Boolean(REGISTER_STATUS.REGISTERED).toString() && Object.keys(rowSelected).length > 0))}
                    onClick={() => {
                        let listRowSelected = Object.keys(rowSelected).map(key => Number(key));
                        handleClickRegister(listRowSelected, true);
                    }}
                    className='w-full md:h-12 md:w-52'
                    variant='outlineOrange3'
                    size='md'
                    iconId='#icon-register'
                    widthIcon={24}
                    heightIcon={16}
                >
                    {t('button.register')}
                </ButtonCusWithIcon>
            </>
        );
    };

    return (
        <div className='w-full bg-white layout-card rounded-t-none flex flex-col gap-4 md:gap-6'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='grid md:grid-cols-4 grid-cols-2 md:gap-3 gap-2'>
                        <FormField
                            control={form.control}
                            name='regionId'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.region')}
                                            search={false}
                                            value={field.value ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                                form.setValue('regionId', item?.code ?? '');
                                                form.setValue('provinceId', '');
                                                handleFilterProvince(item?.code);
                                            }}
                                            items={
                                                (Array.isArray(regions) && regions?.length ?
                                                    [
                                                        ...regions
                                                            .filter(item => item.name)
                                                            .map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='provinceId'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.province')}
                                            search={false}
                                            value={field.value ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                                form.setValue('provinceId', item?.code ?? '');
                                            }}
                                            items={
                                                (Array.isArray(provinces) && provinces?.length ?
                                                    [
                                                        ...provinces
                                                            .filter(item => item.name)
                                                            .map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='locationId'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.location_name')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value);
                                            }}
                                            items={
                                                locations && locations.length > 0 ?
                                                    locations.map(item => ({
                                                        label: item.name,
                                                        value: item.id.toString(),
                                                        code: item.name
                                                    })) :
                                                    []
                                            }
                                            search={true}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='isRegis'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            disableClearIcon={true}
                                            placeholder={t('field.status')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? null);
                                                setRowSelected({});
                                            }}
                                            items={
                                                REGISTER_STATUS_LIST.map(item => ({
                                                    label: t(item.name),
                                                    value: item.value.toString()
                                                }))
                                            }
                                            search={false}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                    </div>
                    <div className='w-full flex justify-center mt-4'>
                        <ButtonSearch size='md' className='xsm:max-w-52 max-w-full w-full'>
                            {t('button.search')}
                        </ButtonSearch>
                    </div>
                </form>
            </Form>

            <div className={classNames('flex xsm:flex-row flex-col justify-center w-full xsm:gap-4 gap-2 point-900:hidden')}>
                {renderButtonTable()}
            </div>
            <Table
                columns={columns}
                data={locationBikes}
                state={{
                    ...pagingRegister,
                    rowSelection: rowSelected
                }}
                rowCount={totalRecordRegister}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                enableRowSelection={({ original }) => (original.isRegis.toString() !== '' && enableStatus)}
                onRowSelectionChange={setRowSelected}
                showSelectPageSize={true}
                renderActions={(
                    <div className={cn('point-900:flex hidden point-900:gap-4 gap-2')}>
                        {renderButtonTable()}
                    </div>
                )}
            />
        </div>
    );
});
